import React, { type ReactNode } from "react"

import { type LeadsFilterOptions } from "../../Leads/redux/leads.types.ts"
import { type PermissionLeadPermissions } from "../../SettingsPermissions/types/permissions.types.ts"
import type { Lead, LeadChecklistItem } from "../types/lead.types.ts"
import { LeadActivityTab } from "./activity/LeadActivityTab.tsx"
import { LeadDetailsTab } from "./additional/LeadDetailsTab.tsx"
import { LeadDocumentsTab } from "./documents/LeadDocumentsTab.tsx"
import { leadHasDocumentsTab } from "./documents/lead-documents-visibility.ts"
import { LeadFilesTab } from "./files/LeadFilesTab.tsx"
import { type LeadPatchSender } from "./lead-workspace-patch.ts"
import { LeadOverviewTab } from "./overview/LeadOverviewTab.tsx"
import { LeadScopeTab } from "./scope/LeadScopeTab.tsx"

export type LeadWorkspaceTabKey =
  | "overview"
  | "scope"
  | "activity"
  | "additional"
  | "files"
  | "documents"

export interface LeadWorkspaceTabContext {
  lead: Lead
  canEdit: boolean
  canAssignOwner?: boolean
  saving?: boolean
  filterOptions: LeadsFilterOptions
  leadPermissions: PermissionLeadPermissions
  onPatch: LeadPatchSender
  onChecklistUpdate: (checklist: LeadChecklistItem[]) => Promise<void>
  onLeadSelect: (id: string, action: string) => void
  onStageChange: (targetStatus: number) => void
  onToggleFavorite?: () => void
  onTabChange: (key: LeadWorkspaceTabKey) => void
}

export interface LeadWorkspaceTabDef {
  key: LeadWorkspaceTabKey
  labelKey: string
  /** Short tooltip text behind the tab's ⓘ affordance. */
  descriptionKey: string
  render: (context: LeadWorkspaceTabContext) => ReactNode
  /** Omitted means always visible. Evaluated against the current lead on every render. */
  isVisible?: (lead: Lead) => boolean
}

export const LEAD_WORKSPACE_TABS: LeadWorkspaceTabDef[] = [
  {
    key: "overview",
    labelKey: "leadWorkspace.tabs.overview",
    descriptionKey: "leadWorkspace.tabDescriptions.overview",
    render: (context) => <LeadOverviewTab {...context} />
  },
  {
    key: "scope",
    labelKey: "leadWorkspace.tabs.scope",
    descriptionKey: "leadWorkspace.tabDescriptions.scope",
    render: ({ lead, canEdit, filterOptions, onPatch }) => (
      <LeadScopeTab
        lead={lead}
        canEdit={canEdit}
        filterOptions={filterOptions}
        onPatch={onPatch}
      />
    )
  },
  {
    key: "activity",
    labelKey: "leadWorkspace.tabs.activity",
    descriptionKey: "leadWorkspace.tabDescriptions.activity",
    render: ({ lead, leadPermissions }) => (
      <LeadActivityTab lead={lead} leadPermissions={leadPermissions} />
    )
  },
  {
    key: "additional",
    labelKey: "leadWorkspace.tabs.additional",
    descriptionKey: "leadWorkspace.tabDescriptions.additional",
    render: (context) => <LeadDetailsTab {...context} />
  },
  {
    key: "files",
    labelKey: "leadWorkspace.tabs.files",
    descriptionKey: "leadWorkspace.tabDescriptions.files",
    render: ({ lead, canEdit, saving, onPatch }) => (
      <LeadFilesTab
        attachments={lead?.attachments}
        canEdit={canEdit}
        saving={saving}
        onPatch={onPatch}
      />
    )
  },
  {
    key: "documents",
    labelKey: "leadWorkspace.tabs.documents",
    descriptionKey: "leadWorkspace.tabDescriptions.documents",
    isVisible: leadHasDocumentsTab,
    render: ({ lead, canEdit }) => (
      <LeadDocumentsTab lead={lead} canEdit={canEdit} />
    )
  }
]
